/**
 * Propiedades avanzadas del editor de plantillas: bloqueo, alineación a la página,
 * páginas donde se imprime, ajuste de texto, vínculo de campo dinámico y editor de
 * la tabla dinámica (fuente de datos, columnas, fuente, alturas, encabezado).
 */
import { LockIcon, LockOpenIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";

const cls = "w-full bg-app border border-border rounded px-2 py-1 text-xs";
const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">{children}</label>;

export function LockAlignBar({ el, layout, onChange }) {
  const ml = layout.margin_left ?? 40, mr = layout.margin_right ?? 40;
  const mt = layout.margin_top ?? 40, mb = layout.margin_bottom ?? 40;
  const pw = layout.page_width, ph = layout.page_height;
  const btn = "text-[10px] px-1.5 py-1 border border-border rounded hover:border-agro-green hover:text-agro-green";
  return (
    <div className="mb-3 space-y-2">
      <button onClick={() => onChange({ locked: !el.locked })} data-testid="prop-lock"
        className={`w-full text-xs px-2 py-1 border rounded flex items-center justify-center gap-1.5 ${el.locked ? "border-agro-yellow text-agro-yellow" : "border-border text-text-secondary"}`}>
        {el.locked ? <LockIcon size={12} /> : <LockOpenIcon size={12} />} {el.locked ? "Bloqueado (no se mueve)" : "Bloquear posición"}
      </button>
      <div>
        <Lbl>Alinear en la página (dentro de márgenes)</Lbl>
        <div className="grid grid-cols-3 gap-1">
          <button className={btn} data-testid="align-left" onClick={() => onChange({ x: ml })}>Izq.</button>
          <button className={btn} data-testid="align-hcenter" onClick={() => onChange({ x: Math.round((pw - el.w) / 2) })}>Centro</button>
          <button className={btn} data-testid="align-right" onClick={() => onChange({ x: Math.round(pw - mr - el.w) })}>Der.</button>
          <button className={btn} onClick={() => onChange({ y: mt })}>Arriba</button>
          <button className={btn} onClick={() => onChange({ y: Math.round((ph - el.h) / 2) })}>Medio</button>
          <button className={btn} onClick={() => onChange({ y: Math.round(ph - mb - el.h) })}>Abajo</button>
        </div>
      </div>
      {el.type !== "table" && (
        <div>
          <Lbl>Imprimir en</Lbl>
          <select value={el.page_scope || "auto"} onChange={(e) => onChange({ page_scope: e.target.value })}
            className={cls} data-testid="prop-page-scope">
            <option value="auto">Automático (debajo de la tabla = última página)</option>
            <option value="all">Todas las páginas</option>
            <option value="first">Solo primera página</option>
            <option value="last">Solo última página</option>
          </select>
        </div>
      )}
    </div>
  );
}

export function TextExtras({ el, onChange, fields }) {
  const flat = (fields || []).flatMap((g) => g.campos.filter((c) => c.tipo !== "table" && !c.key.startsWith("__"))
    .map((c) => ({ ...c, grupo: g.grupo })));
  return (
    <>
      {el.type === "variable" && (
        <div className="mb-2.5">
          <Lbl>Campo dinámico vinculado</Lbl>
          <select value={el.var_key || ""} data-testid="prop-var-key"
            onChange={(e) => {
              const k = e.target.value;
              const old = el.var_key ? `{${el.var_key}}` : null;
              const text = old && (el.text || "").includes(old) ? el.text.replace(old, `{${k}}`) : `{${k}}`;
              onChange({ var_key: k, text });
            }} className={cls}>
            <option value="">— Ninguno —</option>
            {flat.map((c) => <option key={`${c.grupo}-${c.key}`} value={c.key}>{c.grupo}: {c.label}</option>)}
          </select>
          <p className="text-[10px] text-text-muted mt-1">Puedes agregar texto fijo alrededor, p. ej. &quot;RTN: {"{cliente_rtn}"}&quot;.</p>
        </div>
      )}
      <label className="flex items-center gap-2 text-[11px] text-text-secondary mb-2">
        <input type="checkbox" checked={el.wrap !== false} onChange={(e) => onChange({ wrap: e.target.checked })} data-testid="prop-wrap" />
        Ajustar texto al ancho (si no, una línea recortada)
      </label>
    </>
  );
}

export function TableEditor({ el, onChange, tables, fonts }) {
  const src = (tables || []).find((t) => t.data_key === (el.data_key || "items")) || (tables || [])[0];
  const colsAvail = src?.columnas || [];
  const cols = el.columns || [];
  const setCol = (i, p) => onChange({ columns: cols.map((c, k) => (k === i ? { ...c, ...p } : c)) });
  const total = cols.reduce((s, c) => s + Number(c.width_pct || 0), 0);
  return (
    <div className="space-y-2" data-testid="table-editor">
      <div>
        <Lbl>Fuente de datos</Lbl>
        <select value={el.data_key || "items"} onChange={(e) => onChange({ data_key: e.target.value })} className={cls} data-testid="prop-data-key">
          {(tables || []).map((t) => <option key={t.data_key} value={t.data_key}>{t.label}</option>)}
        </select>
      </div>
      <Lbl>Columnas (ancho total {total}%)</Lbl>
      {cols.map((c, i) => (
        <div key={i} className="border border-border/60 rounded p-1.5 space-y-1">
          <div className="flex gap-1">
            <select value={c.key} onChange={(e) => setCol(i, { key: e.target.value })} className={cls}>
              {colsAvail.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              {!colsAvail.some((a) => a.key === c.key) && <option value={c.key}>{c.key}</option>}
            </select>
            <button onClick={() => onChange({ columns: cols.filter((_, k) => k !== i) })} className="px-1 text-text-muted hover:text-red-400" title="Quitar columna">
              <TrashIcon size={12} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            <input value={c.label} onChange={(e) => setCol(i, { label: e.target.value })} className={`${cls} col-span-3`} placeholder="Encabezado" />
            <input type="number" value={c.width_pct} onChange={(e) => setCol(i, { width_pct: Number(e.target.value) })} className={cls} title="Ancho %" />
            <select value={c.align || "left"} onChange={(e) => setCol(i, { align: e.target.value })} className={`${cls} col-span-2`}>
              <option value="left">Izquierda</option><option value="center">Centro</option><option value="right">Derecha</option>
            </select>
          </div>
        </div>
      ))}
      <button onClick={() => onChange({ columns: [...cols, { key: colsAvail[0]?.key || "descripcion", label: colsAvail[0]?.label?.toUpperCase() || "COLUMNA", width_pct: 20, align: "left" }] })}
        className="w-full text-[11px] border border-dashed border-border rounded py-1 flex items-center justify-center gap-1 hover:border-agro-green" data-testid="add-column">
        <PlusIcon size={11} /> Agregar columna
      </button>
      <div className="grid grid-cols-2 gap-2">
        <div><Lbl>Fuente</Lbl>
          <select value={el.font || "Helvetica"} onChange={(e) => onChange({ font: e.target.value })} className={cls}>
            {fonts.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select></div>
        <div><Lbl>Tamaño</Lbl><input type="number" step="0.5" value={el.font_size || 9} onChange={(e) => onChange({ font_size: Number(e.target.value) })} className={cls} /></div>
        <div><Lbl>Alto fila (px)</Lbl><input type="number" value={el.row_height || 18} onChange={(e) => onChange({ row_height: Number(e.target.value) })} className={cls} /></div>
        <div><Lbl>Alto encabezado</Lbl><input type="number" value={el.header_height || 22} onChange={(e) => onChange({ header_height: Number(e.target.value) })} className={cls} /></div>
        <div><Lbl>Fondo encabezado</Lbl><input type="color" value={el.header_fill || "#F3F4F6"} onChange={(e) => onChange({ header_fill: e.target.value })} className="w-full h-7 bg-app border border-border rounded" /></div>
        <div><Lbl>Texto encabezado</Lbl><input type="color" value={el.header_color || "#000000"} onChange={(e) => onChange({ header_color: e.target.value })} className="w-full h-7 bg-app border border-border rounded" /></div>
      </div>
      <label className="flex items-center gap-2 text-[11px] text-text-secondary">
        <input type="checkbox" checked={el.show_header !== false} onChange={(e) => onChange({ show_header: e.target.checked })} /> Mostrar encabezado
      </label>
      <label className="flex items-center gap-2 text-[11px] text-text-secondary">
        <input type="checkbox" checked={el.show_grid !== false} onChange={(e) => onChange({ show_grid: e.target.checked })} /> Mostrar líneas
      </label>
      <p className="text-[10px] text-text-muted">
        La tabla crece hacia abajo dentro de su caja; si no caben las filas continúan en la página siguiente
        (encabezado repetido) y los totales/firma/pie ubicados debajo se imprimen solo en la última página.
      </p>
    </div>
  );
}
